/**
 * The collapsed sources rail — §2.8 #95.
 *
 * Extracted from `SourcesPanel`, where it was an early `return` roughly 90
 * lines long sitting in the middle of the expanded panel's setup.
 *
 * 60px wide, so everything is an icon. The three things it still has to
 * convey: which version you are on, how many sources there are, and which tab
 * you would land on if you expanded. Clicking either icon expands the panel
 * AND selects that tab, which is why `onExpandWithTab` takes the tab rather
 * than the rail owning a separate toggle.
 *
 * The count caps at `9+`. Not cosmetic: the badge is a 16px circle, and a
 * three-digit number in it is unreadable at any size. The exact count is one
 * click away.
 */
import { Button } from '@ai-tutor/ui';
import { Brain, FolderOpen, Sidebar } from 'lucide-react';

export type SourcesPanelTab = 'sources' | 'context';

export interface SourcesPanelCollapsedProps {
  /** Version label shown in the chip. */
  currentVersion: string;
  /** How many sources the expanded panel would list. */
  sourceCount: number;
  /** Which tab is selected, so the rail marks it. */
  activeTab: SourcesPanelTab;
  onToggleCollapse?: () => void;
  onExpandWithTab: (tab: SourcesPanelTab) => void;
}

export function SourcesPanelCollapsed({
  currentVersion,
  sourceCount,
  activeTab,
  onToggleCollapse,
  onExpandWithTab,
}: SourcesPanelCollapsedProps) {
  return (
    <div className='flex h-full flex-col items-center space-y-3 py-4'>
      <Button
        variant='ghost'
        size='sm'
        onClick={onToggleCollapse}
        aria-label='Expand sources panel'
        className='h-10 w-10 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
      >
        <Sidebar className='h-4 w-4' aria-hidden='true' />
      </Button>

      <div className='h-4 w-px bg-secondary' />

      <div className='flex flex-col items-center space-y-1'>
        <div className='flex h-7 w-10 items-center justify-center rounded-md border border-border bg-muted dark:border-gray-600'>
          <span className='font-mono text-xs font-medium text-foreground'>
            {currentVersion}
          </span>
        </div>
        <div className='h-px w-8 bg-secondary' />
      </div>

      {/* Real buttons, not clickable divs. The originals were `<div onClick>`
          with a `title` — unreachable by keyboard and nameless to a screen
          reader, in a rail where the icon IS the only label. */}
      <RailButton
        label={`Sources (${sourceCount})`}
        icon={FolderOpen}
        active={activeTab === 'sources'}
        onClick={() => onExpandWithTab('sources')}
        badge={
          sourceCount > 0
            ? sourceCount > 9
              ? '9+'
              : String(sourceCount)
            : null
        }
        // `brand-600`, not `brand-500`. White 12px on brand-500 is 3.41:1 —
        // the badge is the only place a sighted user reads the count, so the
        // `aria-hidden` on it (the number is in the button's name) does not
        // excuse the ratio. brand-600 is 5.03:1 and visually near-identical.
        activeBadgeClass='bg-brand-600 dark:bg-primary dark:text-primary-foreground'
      />

      <RailButton
        label='Prompt'
        icon={Brain}
        active={activeTab === 'context'}
        onClick={() => onExpandWithTab('context')}
      />
    </div>
  );
}

function RailButton({
  label,
  icon: Icon,
  active,
  onClick,
  badge,
  activeBadgeClass,
}: {
  label: string;
  icon: React.ElementType;
  active: boolean;
  onClick: () => void;
  badge?: string | null;
  activeBadgeClass?: string;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      // `aria-current` rather than colour alone — the same fix `LessonTabs`
      // and the notes selector took. The rail marks the active tab with a
      // border tint and a 4px bar, neither of which reaches assistive tech.
      aria-current={active}
      title={label}
      className={`relative flex h-10 w-10 items-center justify-center rounded-lg border transition-all duration-200 ${
        active
          ? 'border-brand-200 bg-brand-50 hover:bg-brand-100 dark:border-primary dark:bg-muted dark:hover:bg-muted'
          : 'border-border/60 bg-card/50 backdrop-blur-sm hover:bg-muted dark:bg-card'
      }`}
    >
      <Icon
        className={`h-4 w-4 ${
          active ? 'text-brand-600 dark:text-primary' : 'text-muted-foreground'
        }`}
        aria-hidden='true'
      />
      {badge && (
        <span
          aria-hidden='true'
          className={`absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs font-medium text-white ${
            active ? activeBadgeClass : 'bg-gray-500'
          }`}
        >
          {badge}
        </span>
      )}
      {active && (
        <span
          aria-hidden='true'
          className='absolute -left-1 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r-full bg-brand-600 dark:bg-primary'
        />
      )}
    </button>
  );
}
